/* eslint-disable max-params */
/* eslint-disable max-statements, max-len */

import { cacheDecorator as cache, invalidateCacheIfSuccessfulDecorator as invalidateCache, invalidateCacheIfSuccessfulDecorator } from '@ds/swymkit/tools'

import { passportFetch } from '~/services/fetch-passport'
import { swymFetch } from '~/services/fetch-swym'

import { SearchService } from '~/entities/user/user/api/search'
import type { IUser, NetworkUser, UserInterest, UserInvitation, UserLanguage, UserLink, UserSchool, UserSkill, UserWork } from '~/entities/user/user/interfaces/user'

import { useFeatureManager } from '~/features-generic/config-manager/composables/feature-manager'
import { useLocalStorageManager } from '~/features-generic/config-manager/utils/local-storage-manager'

import type { NetworkResult, SwymResponse } from '~/interfaces/services-dto'
import { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'

/**
 * Fetch the current logged user.
 * @cache 60s
 * @returns The logged user
 */
export function getCurrentUser(): Promise<SwymResponse<IUser>> {
  return cache(
    'current-user',
    () => swymFetch
      .get('/api/user/getcurrent')
      .json<SwymResponse<IUser>>(),
    60000,
  )
}

/**
 * Fetch a user using his hash_key.
 * @cache 60s
 * @param uid The hash_key of a user
 * @returns The user
 */
export function getUser(hash_key: string) {
  return cache(
    `user-${hash_key}`,
    () => swymFetch
      .post('/api/user/find', { json: { params: { hash_key } } })
      .json<SwymResponse<IUser>>(),
    60000,
  )
}

/**
 * Fetch a user using his login.
 * @cache 60s
 * @param login The login of a user
 * @returns The user
 */
export function getUserByLogin(login: string) {
  return cache(
    `user-login-${login}`,
    () => swymFetch
      .post('/api/user/find', { json: { params: { login } } })
      .json<SwymResponse<IUser>>(),
    60000,
  )
}

/**
 * Fetch the user using the resource uid of a user
 * @cache 60s
 * @param uid The "ds6w:resourceUid" of a user
 * @returns The user received
 */
export function getUserByUid(uid: string) {
  return cache(
    `user-uid-${uid}`,
    () => swymFetch
      .post('/api/user/find', { json: { params: { login: uid } } })
      .json<SwymResponse<IUser>>(),
    60000,
  )
}

/**
 * Fetch the current user preferences regarding the highlighted content display of a community
 * @cache 60s
 * @param community_id The community id
 * @returns The user received
 */
export function getCurrentUserHiddenHlContent(community_id: string) {
  return cache(
    `current-user-hidden-hl-content-${community_id}`,
    () => swymFetch
      .get(`/api/community/${community_id}/user/hiddenHlContent`)
      .json<undefined | SwymResponse<null>>(),
    60000,
  )
}

export function addCurrentUserHiddenHlContent(community_id: string) {
  return swymFetch
    .post(`/api/community/${community_id}/user/hiddenHlContent`, { json: { params: {} } })
    .json<SwymResponse<null>>()
}

/**
 * 3DSwym search users API.
 * @param options - Options.
 * @param options.query - Search query.
 * @param options.communityUri - Community subject URI. (default: undefined)
 *                               If provided will returns the user's community access role
 *                               in each user result.
 * @param options.limit - Number of results per page. (default: 5)
 * @param options.page - Page number to fetch. (default: 0)
 * @param options.locale - (default: 'en')
 * @returns Promise with list matching users.
 */
export function searchUser({
  query,
  communityUri,
  limit = 5,
  page = 0,
  locale = 'en',
}: {
  /** Search query. */
  query: string

  /**
   * Community subject URI.
   * If provided will returns the user's community access role in each user result.
   * @default undefined
   */
  communityUri?: string

  /**
   * Number of results per page.
   * @default 5
   */
  limit?: number

  /**
   * Page number to fetch.
   * @default 0
   */
  page?: number

  /**
   * @default 'en'
   */
  locale?: string
}) {
  return cache(
    `search-user-${communityUri ? `${communityUri}-` : ''}${query}`,
    () => swymFetch
      .post('/api/user/search', {
        json: {
          params: {
            query,
            community_id: communityUri,
            limit,
            start: page,
            locale,
          },
        },
      })
      .json<SwymResponse<IUser[]>>(), // TODO: should parse users results.
    60000,
  )
}

export function getLangList() {
  return cache(
    'lang-list',
    () => swymFetch
      .post('/api/SpokenLanguage/getlanglist')
      .json<SwymResponse<Record<string, string>[]>>()
      .then((response) => {
        // eslint-disable-next-line max-nested-callbacks
        const languageSort = response.result.sort((a, b) => a.lang_english > b.lang_english ? 1 : -1)
        response.result = languageSort
        return response
      }),
    60000 * 60,
  )
}

export function getCountry() {
  return cache(
    'country-list',
    () => passportFetch
      .get('/api/public/countries')
      .json<Record<string, string>[]>(),
    60000 * 60,
  )
}

/**
 * Parse the api response for network by user.
 * @param response - Api response for 'getnetworkbyuser'
 * @returns List of users in the network
 */
function networkUsersParser(response: SwymResponse<NetworkResult<NetworkUser>>) {
  // we need to sort the response and remove duplicates
  // to be removed in 23xFD04 when fix delivered by backend
  const { result } = response
  const { users } = result
  const userMap = new Map<string, NetworkUser>()
  // remove duplicates

  users.forEach((user) => {
    userMap.set(user.login, user)
  })
  const nonDuplicateUsers = Array.from(userMap.values())
  // sort users
  const lowerCaseLastName = (a: NetworkUser) => a.last_name.toLowerCase()
  const lowerCaseFirstName = (a: NetworkUser) => a.first_name.toLowerCase()
  nonDuplicateUsers.sort((a, b) => {
    const lastNameComparisonValue = lowerCaseLastName(a)
      .localeCompare(lowerCaseLastName(b)) // -1, 0, 1
    // compare first names if last names are equal
    if (lastNameComparisonValue === 0) {
      return lowerCaseFirstName(a)
        .localeCompare(lowerCaseFirstName(b))
    }

    else { return lastNameComparisonValue }
  })
  // add the sorted users back to the result & response
  result.users = nonDuplicateUsers

  return response
}

/**
 * To make a API call to '/api/network/getnetworkbyuser'
 * @param hash_key - Hash key of the user
 * @param limit - Maximum number of users to fetch.
 * @param page - Current page number
 * @returns A list of users present in network list
 */
function getNetworkByUserAPICall(hash_key: string, limit?: number, page?: number) {
  return swymFetch
    .post('/api/network/getnetworkbyuser', { json: { params: { hash_key, limit, page } } })
    .json<SwymResponse<NetworkResult<NetworkUser>>>()
    .then(networkUsersParser)
}

// Used to get user network ==> user hash_key | login needed
export function getNetworkByUser(
  hash_key: string,
  limit?: number,
  page?: number,
) {
  return cache(
    `user-network-${hash_key}-${page}-${limit}`,
    () => getNetworkByUserAPICall(hash_key, limit, page),
    60000,
  )
}

/**
 * Get private communities shared with a user
 */
export function getSharedNonPublicCommunitiesByUser(
  limit: number,
  page: number,
  hash_key: string,
) {
  return cache(
    `user-shared-non-public-communities-${hash_key}`,
    () => swymFetch
      .post('/api/community/getsharednonpubliccommunities', { json: { params: { limit, page, hash_key } } })
      .json<SwymResponse<IUser>>(),
    60000,
  )
}

/**
 * Get invitation status (accepted, pending...)
 */
export function getInvitationStatus(hash_key?: string, uid?: string) {
  return cache(
    `user-invitation-status-${hash_key}`,
    () => swymFetch
      .post('/api/network/getinvitationstatus', { json: { params: { hash_key, uid } } })
      .json<SwymResponse<UserInvitation>>(),
    2000,
  )
}

/**
 * Get people you may know, users recomendation to the curent user
 */
export function getPeopleFamiliarToCurrentUser(
  idsToFilterArr: string[],
  itemType: string,
  limit: number,
  maxNbOfCommonElements: string,
  mode: string,
) {
  return cache(
    'current-user-recomandations',
    () => swymFetch
      .post('/api/Recommendation/getpeoplefamiliartocurrentuser', {
        json: { params: { idsToFilterArr, itemType, limit, maxNbOfCommonElements, mode } },
      })
      .json<SwymResponse<IUser>>(),
    60000,
  )
}

/**
 * Get contacts shred between current user and user selected
 */
export function getMySharedContacts(hash_key: string) {
  return cache(
    `my-shared-contacts-${hash_key}`,
    () => swymFetch
      .post('/api/network/getmysharedcontacts', { json: { params: { hash_key } } })
      .json<SwymResponse<IUser>>(),
    60000,
  )
}

/**
 * Get fields informations like readonly, value and fieldconstraint
 */
export function getFields() {
  return cache(
    'fields',
    () => passportFetch
      .get('/api/authenticated/user/fields?xrequestedwith=xmlhttprequest')
      .json<SwymResponse<object>>(),
    60000,
  )
}

/**
 * Update user name
 * @param firstName
 * @param lastName
 * @param username
 * @returns
 */
export function editName(
  firstName: string,
  lastName: string,
  username: string,
) {
  const plop = invalidateCacheIfSuccessfulDecorator(`user-${username}`, async () => {
    return await passportFetch
      .post('/api/authenticated/user/fields', { json: { fields: { firstName, lastName, username } } })
      .json()
  })
  return plop
}

/**
 * Update phone
 * @param telephone
 * @param username
 * @returns
 */
export function updatePhone(telephone: string, username: string) {
  return passportFetch
    .post('/api/authenticated/user/fields', { json: { fields: { telephone, username } } })
    .json()
}

/**
 * Update email
 */
export function editEmail(email: string, username: string) {
  return passportFetch
    .post('/api/authenticated/user/fields', { json: { fields: { email, username } } })
    .json()
}

/**
 * Update location
 */
export function editLocation(data: { company?: string, address?: string, city?: string, zip?: string, country?: string, username: string }) {
  return passportFetch
    .post('/api/authenticated/user/fields', { json: { fields: { company: data.company, address: data.address, city: data.city, zip: data.zip, country: data.country, username: data.username } } })
    .json()
}

/**
 * Get subscription status on a given subject
 * @param subject_uri Which subject should be checked
 * @returns
 */
export function getSubscription(subject_uri: SUBJECT_6W_URI) {
  return cache(`subscription-${subject_uri}`, () => swymFetch
    .get(`/api/user/subscriptions/${subject_uri}`)
    .json<SwymResponse<undefined | Record<string, string>>>())
}

/**
 * Subscribe to a subject update
 * @param subject_uri Which subject should be subscribe
 * @returns
 */
export function addSubscription(subject_uri: SUBJECT_6W_URI) {
  return invalidateCache(`subscription-${subject_uri}`, () => swymFetch
    .post(`/api/user/subscriptions/${subject_uri}`))
}

/**
 * Delete a subscription to a topic
 * @param subject_uri Which subject should be unsubscribe
 * @returns
 */
export function removeSubscription(subject_uri: SUBJECT_6W_URI) {
  return invalidateCache(`subscription-${subject_uri}`, () => swymFetch
    .delete(`/api/user/subscriptions/${subject_uri}`))
}

/**
 * Upload a new profile picture
 * @param base64_encoded
 * @param file_content
 * @returns
 */
export function uploadPreviewPicture(base64_encoded: boolean, file_content: string) {
  return swymFetch
    .post('/api/user/uploadpreviewpicture', { json: { params: { base64_encoded, file_content } } })
    .json<SwymResponse<string>>()
}

/**
 * Update a new profile picture
 * @param filekey
 * @param filename
 * @returns
 */
export function uploadPicture(filekey: string, filename: string) {
  return swymFetch
    .post('/api/user/uploadpicture', { json: { params: { filekey, filename } } })
    .json()
}

/**
 * Get the user profile picture
 * @param filekey
 * @returns
 */
export function getpreviewpicture(filekey: string) {
  return swymFetch
    .get(`/apiuser/getpreviewpicture/filekey/${filekey}`)
    .json()
}

/**
 * Update the user summary
 * @returns
 */
export function saveResume(user_infos: string | undefined) {
  return swymFetch
    .post('/api/user/saveuserinfos', { json: { params: { user_infos } } })
}

/**
 * Create a website
 * @returns
 */
export function addSites(
  url: string,
  type: string,
  title: string,
  description: string,
  update: string,
) {
  return swymFetch
    .post('/api/Link/add', { json: { params: { url, type, title, description, update } } })
    .json<SwymResponse<UserLink>>()
}

/**
 * Update an existing website
 * @returns
 */
export function editSite(
  url: string,
  type: string,
  title: string,
  description: string,
  update: string,
  id: string,
) {
  return swymFetch
    .post('/api/link/update', { json: { params: { url, type, title, description, update, id } } })
    .json<SwymResponse<UserLink>>()
}

/**
 * Delete an existing website
 * @returns
 */
export function removeSite(id: string) {
  return swymFetch
    .post('/api/link/delete', { json: { params: { id } } })
}

/**
 * Create an interest
 * @returns
 */
export function createInterest(title: string, update: string) {
  return swymFetch
    .post('/api/interest/create', { json: { params: { title, update, description: '' } } })
    .json<SwymResponse<UserInterest>>()
}

/**
 * Delete an existing interest
 * @returns
 */
export function deleteInterest(id: string) {
  return swymFetch
    .post('/api/interest/delete', { json: { params: { id } } })
}

/**
 * Create a language
 * @returns
 */
export function createLanguage(data: { description: string, iso_code: string, lang_english: string, lang_native: string, language_id: string, level: number, translated: string, update: string }) {
  return swymFetch
    .post('/api/SpokenLanguage/create', { json: { params: { description: data.description, iso_code: data.iso_code, lang_english: data.lang_english, lang_native: data.lang_native, language_id: data.language_id, level: data.level, update: data.update } } })
    .json<SwymResponse<UserLanguage>>()
}

/**
 * Update an existing language
 * @returns
 */
export function updateLanguage(data: { description: string, language_id: string, level: number, update: string, id: string }) {
  return swymFetch
    .post('/api/SpokenLanguage/update', { json: { params: { description: data.description, language_id: data.language_id, level: data.level, update: data.update, id: data.id } } })
    .json<SwymResponse<UserLanguage>>()
}

/**
 * Delete an existing language
 * @returns
 */
export function deleteLanguage(id: string) {
  return swymFetch
    .post('/api/SpokenLanguage/delete', { json: { params: { id } } })
}

/**
 * Create a skill
 * @returns
 */
export function createSkill(
  title: string,
  level: number,
  description: string,
  update: string,
) {
  return swymFetch
    .post('/api/skill/create', { json: { params: { title, level, description, update } } })
    .json<SwymResponse<UserSkill>>()
}

/**
 * Update an existing skill
 * @returns
 */
export function updateSkill(
  title: string,
  level: number,
  description: string,
  update: string,
  id: string,
) {
  return swymFetch
    .post('/api/skill/update', { json: { params: { title, level, description, update, id } } })
    .json<SwymResponse<UserSkill>>()
}

/**
 * Delete an existing skill
 * @returns
 */
export function deleteSkill(id: string) {
  return swymFetch
    .post('/api/skill/delete', { json: { params: { id } } })
}

/**
 * Create an experience
 * @returns
 */
export function createExperience(
  title: string,
  work_name: string,
  industry: string | null,
  place: string | null,
  description: string,
  date_begin: string,
  date_end: string | null,
  not_finished: string,
  update: string,
) {
  return swymFetch
    .post('/api/Work/create', { json: { params: { title, work_name, industry, place, description, date_begin, date_end, not_finished, update } } })
    .json<SwymResponse<UserWork>>()
}

/**
 * Update an existing experience
 * @returns
 */
export function updateExperience(
  title: string,
  work_name: string,
  industry: string | null,
  place: string | null,
  description: string,
  date_begin: string,
  date_end: string | null,
  not_finished: string,
  update: string,
  id: string,
) {
  return swymFetch
    .post('/api/Work/update', { json: { params: { title, work_name, industry, place, description, date_begin, date_end, not_finished, update, id } } })
    .json<SwymResponse<UserWork>>()
}

/**
 * Delete an experience
 * @param id
 * @returns
 */
export function deleteExperience(id: string) {
  return swymFetch
    .post('/api/Work/delete', { json: { params: { id } } })
}

/**
 * Create an education
 * @returns
 */
export function createEducation(
  degree: string,
  description: string,
  place: string,
  school_name: string,
  not_finished: string,
  field_of_study: string,
  date_begin: string,
  date_end: string,
  update: string,
) {
  return swymFetch
    .post('/api/Education/create', { json: { params: { degree, description, place, school_name, not_finished, field_of_study, date_begin, date_end, update } } })
    .json<SwymResponse<UserSchool>>()
}

/**
 * Update an existing education
 * @returns
 */
export function updateEducation(
  degree: string,
  description: string,
  place: string,
  school_name: string,
  not_finished: string,
  field_of_study: string,
  date_begin: string,
  date_end: string,
  update: string,
  id: string,
) {
  return swymFetch
    .post('/api/Education/update', { json: { params: { degree, description, place, school_name, not_finished, field_of_study, date_begin, date_end, update, id } } })
    .json<SwymResponse<UserSchool>>()
}

/**
 * Delete an education
 * @param id
 * @returns
 */
export function deleteEducation(id: string) {
  return swymFetch
    .post('/api/Education/delete', { json: { params: { id } } })
}

/**
 * Remove a contact suggestion
 * @param itemToBlacklistId item id to blacklist
 * @param itemType ???
 * @returns
 */
export function removeSuggestion(itemToBlacklistId: string, itemType: string) {
  return swymFetch
    .post('/api/Recommendation/blacklistitem?xrequestedwith=xmlhttprequest', {
      json: {
        params: {
          itemToBlacklistId,
          itemType,
          limit: 6,
          maxNbOfCommonElements: '5',
          mode: 'offline',
        },
      },
    })
}

export function updateVisibility(visibility: string) {
  return swymFetch
    .post('/api/user/setvisibility?xrequestedwith=xmlhttprequest', { json: { params: { visibility } } })
}

/**
 * Request someone to be in your contact list
 * @param message message to send to the user
 * @param recipient_hash hash_key of the added user
 * @param related_model_id ???
 * @returns
 */
export function addContact(
  message: string,
  recipient_hash: string,
  // related_model_id?: boolean,
) {
  return swymFetch
    .post('/api/notification/send', { json: { params: { do: 'invite', message, recipient_hash, related_model_id: null, type: 'Network' } } })
}

/**
 * Get the status of contact requests
 * @param hash_key hash_key of the user (MAND if login is unset)
 * @param login login of the user (MAND if hash_key is unset)
 * @returns
 */
export function checkStatus(hash_key: string, login: string) {
  return swymFetch
    .post('/api/network/getinvitationstatus', { json: { params: { hash_key, login } } })
    .json<SwymResponse<UserInvitation>>()
}

// USER GROUP
interface CachedUserGroup {
  name: string
  isMember: boolean
  cacheDate: string
}
let featureGroupCache: CachedUserGroup[] = []
const FEATURE_GROUP_KEY = 'feature-groups'
const FEATURE_GROUP_CACHE_DURATION = 6 // in hours
export const userGroup = {
  async isMember(UID: string, groupName: string) {
    const LocalStorageManagerForUserGroup = useLocalStorageManager<CachedUserGroup[]>(
      FEATURE_GROUP_KEY,
      [],
    )
    if (useFeatureManager('search.sources.usersgroup').value) {
      featureGroupCache = LocalStorageManagerForUserGroup.value
      const group = featureGroupCache?.find(e => e.name === groupName)
      // group is cached
      if (group) {
        const date: Date = new Date(group.cacheDate)

        // https://stackoverflow.com/questions/19225414/how-to-get-the-hours-difference-between-two-date-objects
        const deltaInHoursFromNow = Math.abs(Date.now() - date.getTime()) / 36e5

        // if the cached date is older than 6 hours, let's make the call again
        if (deltaInHoursFromNow > FEATURE_GROUP_CACHE_DURATION) {
          const isUserMember = await SearchService.isUserGroupMember(UID, groupName)

          // updating cache information
          group.isMember = isUserMember
          group.cacheDate = new Date()
            .toISOString()
          LocalStorageManagerForUserGroup.value = featureGroupCache

          return isUserMember
        }
        else {
          return group.isMember
        }
      }
      else {
        // adding information to the cache & evaluate if user is a member of the userGroup
        const isUserMember = await SearchService.isUserGroupMember(UID, groupName)

        const newGroup = {
          name: groupName,
          isMember: isUserMember,
          cacheDate: new Date()
            .toISOString(),
        }
        featureGroupCache.push(newGroup)
        LocalStorageManagerForUserGroup.value = featureGroupCache
        return isUserMember
      }
    }
    else {
      return false
    }
  },
  async isUserGroupAvailable(groupName: string) {
    if (useFeatureManager('search.sources.usersgroup').value)
      return await SearchService.getUserGroup(groupName)
    else
      return false
  },
}
